import React from "react";
import NextImage from "next/image";
import { Flex, Text } from "@radix-ui/themes";
import { cn, generateImageUrlFromKey } from "@/lib/utils";
import LotPreviewClock from "./LotPreviewClock";
import FollowCounter from "./FollowCounter";
import { LotLiveData } from "@/app/api/auctions/[auctionId]/gridData/route";
import { secondsToMilliseconds } from "date-fns";
import { AuctionState, AuctionType, Role } from "@prisma/client";
import { Skeleton } from "@/app/components/ui/skeleton";
import { WinningStatus, WinStatus } from "@/lib/enum/winStatus";
import LeadingBar from "./LeadingBar";
import { useSession } from "next-auth/react";

interface Props {
  hover: boolean;
  imageKey: string | undefined;
  isBuyer: boolean;
  polledData: LotLiveData;
  auctionStart: Date;
  auctionState: AuctionState;
  auctionType: AuctionType;
  loading: boolean;
  followers: number;
  following: boolean;
  winningStatus?: WinningStatus;
  compact?: boolean;
  lotId: number;
  auctionId: number;
  userId: string | null;
}

const LotImagePreview = ({
  hover,
  imageKey,
  polledData,
  isBuyer,
  auctionStart,
  auctionState,
  auctionType = AuctionType.ONLINE,
  loading,
  followers,
  following,
  winningStatus,
  compact = false,
  lotId,
  auctionId,
  userId,
}: Props) => {
  const heightStyle = {
    height: compact ? "120px" : `25vw`,
    minHeight: compact ? "120px" : `300px`,
    maxWidth: compact ? "120px" : "",
  };
  if (imageKey === undefined) {
    return (
      <Flex
        direction="column"
        justify="center"
        align="center"
        className="relative font-normal text-xs w-full h-[350px]"
        style={heightStyle}
      >
        <FollowCounter 
          disabled={!isBuyer}
          follows={followers ?? 0} 
          following={following} 
          lotId={lotId}
          auctionId={auctionId}
          userId={userId}
        />
        <Flex className="flex-grow" align="center">
          No images for this lot.
        </Flex>
        {!compact && (
          <LeadingBar winningStatus={winningStatus} polledData={polledData} />
        )}
        <LotPreviewClock
          endDate={polledData.endDate}
          auctionStart={auctionStart}
          auctionState={auctionState}
          auctionType={auctionType}
        />
      </Flex>
    );
  } else if (loading) {
    return (
      <Flex
        className="h-[350px] w-full overflow-hidden "
        style={{ ...heightStyle, position: "relative" }}
      >
        <Skeleton className="w-full h-full rounded-none" />
      </Flex>
    );
  } else {
    return (
      <Flex
        className={cn(
          compact
            ? "h-[120px] w-[120px]"
            : "h-[350px] w-full overflow-hidden"
        )}
        style={{ ...heightStyle, position: "relative" }}
      >
        {/* Image wrapper keeps the image clipped, while the outer stays overflow-visible in compact */}
        <Flex className="relative h-full w-full overflow-hidden">
          <FollowCounter 
            disabled={!isBuyer}
            follows={followers ?? 0} 
            following={following} 
            lotId={lotId}
            auctionId={auctionId}
            userId={userId}
          />
          <NextImage
            src={generateImageUrlFromKey(imageKey)}
            alt="Image Preview"
            fill
            unoptimized
            style={{ objectFit: "scale-down", overflow: "hidden" }}
            className={hover ? "scale-103" : ""}
          />
        </Flex>
        {!compact && (
          <LeadingBar winningStatus={winningStatus} polledData={polledData} />
        )}
        <LotPreviewClock
          endDate={polledData.endDate}
          auctionStart={auctionStart}
          auctionState={auctionState}
          auctionType={auctionType}
        />
      </Flex>
    );
  }
};

export default LotImagePreview;
